import { useQuery } from '@lumapps/base-api/react-query';
import { get } from '@lumapps/constants';
import { contentQueryKeys, get as getContent, CONTENT_LIVE_REVISION_FIELDS } from '@lumapps/contents/api/contents';
import { actions as contentActions } from '@lumapps/contents/ducks/slice';
import type { WriteModeContent } from '@lumapps/contents/types';
import { useDispatch } from '@lumapps/redux/react';

const constants = get();

/**
 * Retrieves the publication informations of a content.
 *
 * @param contentId the id of the content we want to retrieve the publication informations
 * @param instanceId the instanceId of the content we want to retrieve the publication informations
 * @returns The query
 */
export const useLiveContentRevision = ({ contentId, instanceId }: { contentId?: string; instanceId: string }) => {
    const dispatch = useDispatch();
    const isEnabled = !!contentId;
    const liveRevision = useQuery({
        enabled: isEnabled,
        queryKey: contentQueryKeys.getLiveRevision(contentId!),
        queryFn: async () => {
            if (contentId) {
                try {
                    const { data } = await getContent(
                        {
                            action: 'PAGE_READ',
                            fields: CONTENT_LIVE_REVISION_FIELDS,
                            uid: contentId,
                            instance: instanceId,
                        },
                        false,
                    );

                    if (constants.isLegacyContext) {
                        dispatch(contentActions.setContent({ id: contentId, liveRevision: data } as WriteModeContent));
                    }
                    return data;
                } catch (error) {
                    // If live revision is in error, it's most likely because the content is not LIVE, then there is no Live version
                    dispatch(contentActions.setContent({ id: contentId, liveRevision: undefined } as WriteModeContent));
                }
            }
            return undefined;
        },
    });

    return isEnabled ? liveRevision : { isLoading: false };
};
